import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, FileText, Image, File, Eye, X } from 'lucide-react';
import { Document } from '@/lib/types/completion';
import { useDocuments } from '@/hooks/useDocuments';
import { useToast } from '@/hooks/use-toast';
import { formatBytes } from '@/lib/utils';

interface DocumentViewerProps {
  documents: Document[];
  entityType: 'job' | 'rfq' | 'bid' | 'completion';
  entityId: string;
  onDocumentClick?: (document: Document) => void;
}

interface DocumentModalProps {
  document: Document | null;
  open: boolean;
  onClose: () => void;
  onDownload: (document: Document) => void;
}

const DocumentModal: React.FC<DocumentModalProps> = ({
  document,
  open,
  onClose,
  onDownload
}) => {
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { getDownloadUrl } = useDocuments({ 
    entityType: document?.entity_type || 'job', 
    entityId: document?.entity_id || '' 
  });

  React.useEffect(() => {
    if (document && open) {
      loadDocument();
    }
  }, [document, open]);

  const loadDocument = async () => {
    if (!document) return;
    
    setLoading(true);
    try {
      const url = await getDownloadUrl(document);
      setDownloadUrl(url);
    } catch (error) {
      console.error('Error loading document:', error);
    } finally {
      setLoading(false);
    }
  };

  const canPreview = document?.content_type.startsWith('image/') || 
                    document?.content_type === 'application/pdf';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="truncate">
              {document?.original_filename}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => document && onDownload(document)}
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <p>Loading document...</p>
            </div>
          )}

          {!loading && downloadUrl && canPreview && (
            <div className="w-full h-full min-h-[500px]">
              {document?.content_type.startsWith('image/') ? (
                <img 
                  src={downloadUrl} 
                  alt={document.original_filename}
                  className="max-w-full h-auto mx-auto"
                />
              ) : document?.content_type === 'application/pdf' ? (
                <iframe
                  src={downloadUrl}
                  className="w-full h-full min-h-[500px]"
                  title={document.original_filename}
                />
              ) : null}
            </div>
          )}

          {!loading && (!canPreview || !downloadUrl) && (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <File className="h-16 w-16 text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">Preview not available</p>
              <p className="text-muted-foreground mb-4">
                Click download to view this file
              </p>
              <Button onClick={() => document && onDownload(document)}>
                <Download className="h-4 w-4 mr-2" />
                Download File
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  documents,
  entityType,
  entityId,
  onDocumentClick
}) => {
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { getDownloadUrl } = useDocuments({ entityType, entityId });
  const { toast } = useToast();

  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) return <Image className="h-5 w-5" />;
    if (contentType === 'application/pdf') return <FileText className="h-5 w-5" />;
    return <File className="h-5 w-5" />;
  };

  const handleDocumentClick = (document: Document) => {
    setSelectedDocument(document);
    setModalOpen(true);
    onDocumentClick?.(document);
  };

  const handleDownload = async (document: Document) => {
    try {
      const url = await getDownloadUrl(document);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.original_filename;
      link.click();
      
      toast({
        title: "Download started",
        description: document.original_filename,
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Failed to download the file",
        variant: "destructive",
      });
    }
  };

  const formatFileSize = (bytes: number) => {
    return formatBytes(bytes);
  };

  if (documents.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <File className="h-12 w-12 mx-auto mb-2 opacity-50" />
        <p>No documents uploaded</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {documents.map((document) => (
        <div
          key={document.id}
          className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {getFileIcon(document.content_type)}
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{document.original_filename}</p>
              <p className="text-sm text-muted-foreground">
                {formatFileSize(document.size_bytes)} • 
                {new Date(document.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDocumentClick(document)}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDownload(document)}
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}

      <DocumentModal
        document={selectedDocument}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onDownload={handleDownload}
      />
    </div>
  );
};